import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Plus, RefreshCw, KeyRound, UserMinus, UserPlus, Trash2, Copy, Check, Loader2, ShieldAlert, Mail, Pencil, ArrowRight, ScrollText, Sparkles, FileDown,
} from "lucide-react";
import { AddUserModal } from "@/components/admin/AddUserModal";
import { EditUserModal } from "@/components/admin/EditUserModal";
import { ROLES, PLANS, PLAN_STYLES, ROLE_STYLES } from "@/components/admin/constants";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

function CopyableField({ label, value, testIdPrefix }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`${testIdPrefix}-card`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <code className="flex-1 text-xs font-mono text-slate-900 break-all" data-testid={`${testIdPrefix}-value`}>{value}</code>
        <Button type="button" variant="outline" size="sm" onClick={onCopy} className="h-7 px-2 text-xs" data-testid={`${testIdPrefix}-copy`}>
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
        </Button>
      </div>
    </div>
  );
}

function CredentialModal({ open, onOpenChange, payload, title }) {
  if (!payload) return null;
  const { user, temp_password, login_url, email_sent } = payload;
  const fullLoginUrl = window.location.origin + (login_url || "/login");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="credential-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" style={{ color: "#F59E0B" }} />
            {title}
          </DialogTitle>
          <DialogDescription>
            {email_sent
              ? <>An email with these credentials was sent to <span className="font-semibold">{user?.email}</span>. Keep this copy as a backup in case it lands in spam.</>
              : <>Email delivery is not active — copy these credentials now and share them with <span className="font-semibold">{user?.email}</span> over your secure channel. The temp password cannot be retrieved later.</>}
          </DialogDescription>
        </DialogHeader>
        {/* Email-status banner */}
        <div
          className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-[12px] ${
            email_sent
              ? "border-emerald-200 bg-emerald-50 text-emerald-900"
              : "border-amber-200 bg-amber-50 text-amber-900"
          }`}
          data-testid={`credential-email-status-${email_sent ? "sent" : "not-sent"}`}
        >
          <Mail className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
          <span>
            {email_sent
              ? <>Invite email sent. The user will be required to change this password on first login.</>
              : <>No invite email was sent. Share these credentials manually for now.</>}
          </span>
        </div>
        <div className="space-y-3 py-2">
          <CopyableField label="Login URL"        value={fullLoginUrl}   testIdPrefix="cred-url" />
          <CopyableField label="Email"            value={user?.email}    testIdPrefix="cred-email" />
          <CopyableField label="Temporary password" value={temp_password} testIdPrefix="cred-password" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cred-modal-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmModal({ open, onOpenChange, title, message, confirmLabel, onConfirm, danger }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await onConfirm(); onOpenChange(false); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={run} disabled={busy} className={danger ? "bg-red-600 hover:bg-red-700 text-white" : "ai-cta"} data-testid="confirm-modal-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateOrgModal({ open, onOpenChange, onCreated }) {
  const [name, setName] = useState("");
  const [plan, setPlan] = useState("starter");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setName(""); setPlan("starter"); } }, [open]);
  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/organizations", { name, plan });
      toast.success(`Created "${data.organization.name}" on ${plan} plan`);
      onCreated();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create organization.");
    } finally { setBusy(false); }
  };
  const selected = PLANS.find((p) => p.value === plan);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="create-org-modal">
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
          <DialogDescription>Each organization is a fully isolated workspace. Pick a plan — you can change it later.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <Label htmlFor="org-name">Organization name</Label>
            <Input id="org-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., St Mary's Hospital" className="h-10 rounded-lg mt-1.5" data-testid="create-org-name" />
          </div>
          <div>
            <Label htmlFor="org-plan">Plan</Label>
            <select
              id="org-plan" value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm mt-1.5 font-semibold"
              data-testid="create-org-plan"
            >
              {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {selected ? (
              <p className="text-[11px] text-slate-600 mt-1.5">
                <Sparkles className="h-3 w-3 inline mr-1 text-teal-600" />
                {selected.sublabel}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="ai-cta rounded-lg" data-testid="create-org-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Create organization
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OrganizationsPanel({ orgs, onChanged, onDelete }) {
  const [showCreate, setShowCreate] = useState(false);
  const [briefingId, setBriefingId] = useState(null);  // org_id currently being downloaded
  const updatePlan = async (org, plan) => {
    try {
      await api.patch(`/admin/organizations/${org.id}/plan`, { plan });
      toast.success(`${org.name} → ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan`);
      onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change plan.");
    }
  };
  const downloadBrief = async (org) => {
    setBriefingId(org.id);
    try {
      const res = await api.get(`/admin/organizations/${org.id}/negotiation-brief.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rciq_negotiation_brief_${org.name.replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Negotiation brief downloaded — ${org.name}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the brief.");
    } finally { setBriefingId(null); }
  };
  return (
    <div data-testid="admin-orgs-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">Each organization is a fully isolated workspace. Data never crosses org boundaries. Plan controls what features and usage limits the organization gets.</p>
        <Button onClick={() => setShowCreate(true)} className="ai-cta rounded-lg" size="sm" data-testid="admin-create-org">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Create org
        </Button>
      </div>
      <div className="surface-card overflow-hidden" data-testid="admin-orgs-table-card">
        <table className="min-w-full text-sm" data-testid="admin-orgs-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-3">Organization</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Seats</th>
              <th className="px-4 py-3">Active dataset</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orgs.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-slate-400">No organizations yet. Click "Create org" to add your first customer.</td></tr>
            ) : orgs.map((o) => {
              const ps = PLAN_STYLES[o.plan] || PLAN_STYLES.starter;
              const overSeats = o.user_seat_cap != null && o.user_count > o.user_seat_cap;
              return (
                <tr key={o.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`admin-org-row-${o.id}`}>
                  <td className="px-4 py-3 font-semibold text-slate-900">{o.name}</td>
                  <td className="px-4 py-3">
                    <select
                      value={o.plan || "starter"}
                      onChange={(e) => updatePlan(o, e.target.value)}
                      className="h-7 text-[11px] rounded border border-slate-300 px-2 font-semibold uppercase tracking-wider"
                      style={{ color: ps.color, background: ps.bg }}
                      data-testid={`admin-org-plan-${o.id}`}
                    >
                      {PLANS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-700 tabular-nums">
                    <span className={overSeats ? "text-red-600 font-semibold" : ""}>{o.user_count}</span>
                    <span className="text-slate-400"> / {o.user_seat_cap ?? "∞"}</span>
                  </td>
                  <td className="px-4 py-3">
                    {o.has_active_dataset ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Loaded</Badge>
                    ) : (
                      <span className="text-[11px] text-slate-400">No data yet</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-700">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        variant="ghost" size="sm"
                        className="h-7 px-2 text-[11px] text-teal-700 hover:bg-teal-50"
                        disabled={!o.has_active_dataset || briefingId === o.id}
                        title={o.has_active_dataset
                          ? "Download Negotiation Brief PDF — 3 AI talking points per payor. Admin only."
                          : "Customer must upload data before a brief can be generated."}
                        onClick={() => downloadBrief(o)}
                        data-testid={`admin-org-negotiation-brief-${o.id}`}
                      >
                        {briefingId === o.id
                          ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          : <FileDown className="h-3 w-3 mr-1" />}
                        Brief
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(o)}
                        data-testid={`admin-org-delete-${o.id}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <CreateOrgModal open={showCreate} onOpenChange={setShowCreate} onCreated={onChanged} />
    </div>
  );
}

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [credPayload, setCredPayload] = useState(null);
  const [credTitle, setCredTitle] = useState("");
  const [confirmState, setConfirmState] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [tab, setTab] = useState("users");
  const [downloadingBattleCard, setDownloadingBattleCard] = useState(false);
  const [downloadingLean, setDownloadingLean] = useState(false);
  const [downloadingKit, setDownloadingKit] = useState(false);
  const [downloadingDemo, setDownloadingDemo] = useState(false);
  const [downloadingWelcome, setDownloadingWelcome] = useState(false);

  const downloadDemoPlaybook = async () => {
    setDownloadingDemo(true);
    try {
      const res = await api.get("/admin/demo-script.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        a.download = `rciq_demo_playbook_${stamp}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Demo Playbook opened in new tab — use Cmd/Ctrl+S to save.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the demo playbook.");
    } finally { setDownloadingDemo(false); }
  };

  const downloadWelcomePack = async () => {
    setDownloadingWelcome(true);
    try {
      const res = await api.get("/admin/client-welcome-pack.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        a.download = `rciq_client_welcome_pack_${stamp}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Client Welcome Pack opened in new tab — use Cmd/Ctrl+S to save, then email to your client.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the welcome pack.");
    } finally { setDownloadingWelcome(false); }
  };

  const downloadBattleCard = async () => {
    setDownloadingBattleCard(true);
    try {
      const res = await api.get("/admin/founders-battle-card.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) {
        // Popup blocked — fall back to anchor click
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        a.download = `rciq_founders_battle_card_${stamp}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Full Battle Card opened in new tab — use Cmd/Ctrl+S to save.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the battle card.");
    } finally { setDownloadingBattleCard(false); }
  };

  const downloadLeanSellingPoints = async () => {
    setDownloadingLean(true);
    try {
      const res = await api.get("/admin/selling-points-lean.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        a.download = `rciq_selling_points_${stamp}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Top 6 Selling Points opened in new tab — use Cmd/Ctrl+S to save.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the selling points PDF.");
    } finally { setDownloadingLean(false); }
  };

  const downloadMarketingKit = async () => {
    setDownloadingKit(true);
    try {
      const res = await api.get("/admin/marketing-launch-kit.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.target = "_blank";
        a.rel = "noopener";
        const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        a.download = `rciq_marketing_launch_kit_${stamp}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      toast.success("Marketing Launch Kit opened in new tab — use Cmd/Ctrl+S to save.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the marketing kit PDF.");
    } finally { setDownloadingKit(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [u, o] = await Promise.all([api.get("/admin/users"), api.get("/admin/organizations")]);
      setUsers(u.data.users || []);
      setOrgs(o.data.organizations || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load users.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const orgNameById = (id) => orgs.find((o) => o.id === id)?.name || "—";
  const orgPlanById = (id) => (orgs.find((o) => o.id === id)?.plan) || null;

  if (me && me.role !== "admin") {
    return (
      <AppShell>
        <div className="surface-card p-6 max-w-lg mx-auto text-center">
          <ShieldAlert className="h-10 w-10 mx-auto" style={{ color: "#EF4444" }} />
          <h2 className="font-display text-xl text-slate-900 mt-3">Admin access required</h2>
          <p className="text-sm text-slate-600 mt-2">You don't have permission to view the User Access Management screen.</p>
        </div>
      </AppShell>
    );
  }

  const onCreated = (payload) => {
    setCredPayload(payload);
    setCredTitle("Account created — share credentials securely");
    load();
  };
  const onOrgCreated = () => { load(); };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !u.active });
      toast.success(!u.active ? `${u.email} activated` : `${u.email} deactivated — sessions ended`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  };
  const updateRole = async (u, role) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { role });
      toast.success(`${u.email} role updated to ${role.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  };
  const resetPw = async (u) => {
    const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
    setCredPayload({ ...data, user: u });
    setCredTitle("Password reset — share the new temp password");
    load();
  };
  const resendInvite = async (u) => {
    const { data } = await api.post(`/admin/users/${u.id}/resend-invite`);
    setCredPayload({ ...data, user: u });
    setCredTitle("New invite generated");
    load();
  };
  const deleteUser = async (u) => {
    await api.delete(`/admin/users/${u.id}`);
    toast.success(`${u.email} deleted`);
    load();
  };

  return (
    <AppShell>
      <div className="fade-up" data-testid="admin-users-page">
        <div className="flex items-end justify-between mb-7 flex-wrap gap-4">
          <div>
            <span className="label-overline">Admin</span>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-1.5 tracking-tight">
              User Access Management
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Provision client accounts, manage roles, reset passwords, and revoke access. Public signup is disabled.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={downloadWelcomePack}
              disabled={downloadingWelcome}
              className="rounded-lg border-indigo-300 bg-gradient-to-br from-indigo-50 to-violet-50 text-indigo-800 hover:from-indigo-100 hover:to-violet-100 font-semibold"
              title="Client Welcome Pack — customer-facing onboarding PDF. Safe to email to newly-signed clients."
              data-testid="admin-client-welcome-pack"
            >
              {downloadingWelcome
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
              Client Welcome Pack
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={downloadDemoPlaybook}
              disabled={downloadingDemo}
              className="rounded-lg border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 text-amber-800 hover:from-amber-100 hover:to-orange-100 font-semibold"
              title="Demo Playbook — the word-for-word 12-minute demo script. Founder-only, do not share externally."
              data-testid="admin-demo-playbook"
            >
              {downloadingDemo
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
              Demo Playbook
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={downloadMarketingKit}
              disabled={downloadingKit}
              className="rounded-lg border-emerald-300 bg-gradient-to-br from-emerald-50 to-teal-50 text-emerald-800 hover:from-emerald-100 hover:to-teal-100 font-semibold"
              title="90-day Marketing Launch Kit — LinkedIn DMs, post templates, partner pitches. Founder use only."
              data-testid="admin-marketing-launch-kit"
            >
              {downloadingKit
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
              Marketing Kit
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={downloadLeanSellingPoints}
              disabled={downloadingLean}
              className="rounded-lg border-cyan-300 bg-gradient-to-br from-cyan-50 to-teal-50 text-cyan-800 hover:from-cyan-100 hover:to-teal-100 font-semibold"
              title="Lean Top-6 Selling Points PDF. What / How / Why-it-beats-their-system. Founder use only."
              data-testid="admin-selling-points-lean"
            >
              {downloadingLean
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <FileDown className="h-3.5 w-3.5 mr-1.5" />}
              Top 6 Selling Points
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={downloadBattleCard}
              disabled={downloadingBattleCard}
              className="rounded-lg border-teal-300 bg-gradient-to-br from-teal-50 to-cyan-50 text-teal-800 hover:from-teal-100 hover:to-cyan-100 font-semibold"
              title="Full Battle Card — all 15 features + objection prep + buyer personas. Founder use only."
              data-testid="admin-founders-battle-card"
            >
              {downloadingBattleCard
                ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
              Full Battle Card
            </Button>
            <Link to="/admin/audit-log" data-testid="admin-view-audit-log">
              <Button variant="outline" size="sm" className="rounded-lg">
                <ScrollText className="h-3.5 w-3.5 mr-1.5" /> Audit log
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={load} className="rounded-lg" data-testid="admin-users-refresh">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
            </Button>
            <Button onClick={() => setShowAdd(true)} className="ai-cta rounded-lg" data-testid="admin-add-user">
              <Plus className="h-4 w-4 mr-1.5" /> Add user
            </Button>
          </div>
        </div>

        {/* Tab toggle */}
        <div className="inline-flex p-1 bg-slate-100 rounded-lg mb-5" data-testid="admin-tab-toggle">
          <button
            type="button"
            onClick={() => setTab("users")}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md transition-colors ${tab === "users" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="admin-tab-users"
          >
            Users · {users.length}
          </button>
          <button
            type="button"
            onClick={() => setTab("orgs")}
            className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md transition-colors ${tab === "orgs" ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            data-testid="admin-tab-orgs"
          >
            Organizations · {orgs.length}
          </button>
        </div>

        {tab === "orgs" ? (
          <OrganizationsPanel
            orgs={orgs}
            onChanged={load}
            onDelete={(o) => setConfirmState({
              title: "Delete organization?",
              danger: true,
              message: `Permanently delete "${o.name}", all ${o.user_count} user${o.user_count === 1 ? "" : "s"}, and all uploaded data? This cannot be undone.`,
              confirmLabel: "Delete organization",
              run: async () => {
                await api.delete(`/admin/organizations/${o.id}`);
                toast.success(`Deleted "${o.name}"`);
                load();
              },
            })}
          />
        ) : (
        <div className="surface-card overflow-hidden" data-testid="admin-users-table-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-4 py-3">Name / Email</th>
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Last login</th>
                  <th className="px-4 py-3">Invite sent</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center py-10 text-slate-400"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Loading…</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-10 text-slate-400">No users yet. Click "Add user" to provision your first client.</td></tr>
                ) : users.map((u) => {
                  const rs = ROLE_STYLES[u.role] || ROLE_STYLES.client_user;
                  const isSelf = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60" data-testid={`admin-user-row-${u.email}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">{u.name}{isSelf ? <span className="ml-1.5 text-[10px] text-slate-400">(you)</span> : null}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700">
                        {u.organization_id ? (
                          <div>
                            <div className="font-medium text-slate-900" data-testid={`admin-user-org-${u.email}`}>{orgNameById(u.organization_id)}</div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {orgPlanById(u.organization_id) ? (
                                <span
                                  className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                                  style={{
                                    color: (PLAN_STYLES[orgPlanById(u.organization_id)] || PLAN_STYLES.starter).color,
                                    background: (PLAN_STYLES[orgPlanById(u.organization_id)] || PLAN_STYLES.starter).bg,
                                  }}
                                  data-testid={`admin-user-plan-${u.email}`}
                                >
                                  {orgPlanById(u.organization_id)}
                                </span>
                              ) : null}
                              {u.company ? <span className="text-[11px] text-slate-500">{u.company}</span> : null}
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-400 italic">Platform admin</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={u.role}
                          disabled={isSelf || u.email === "demo@rciq.ai"}
                          onChange={(e) => updateRole(u, e.target.value)}
                          className="h-7 text-[11px] rounded border border-slate-300 px-2 font-semibold uppercase tracking-wider"
                          style={{ color: rs.color, background: rs.bg }}
                          data-testid={`admin-user-role-${u.email}`}
                        >
                          {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {u.active ? (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 font-semibold" data-testid={`admin-user-status-${u.email}`}>Active</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800 border-red-200 font-semibold" data-testid={`admin-user-status-${u.email}`}>Inactive</Badge>
                        )}
                        {u.must_change_password ? (
                          <Badge className="ml-1.5 bg-amber-50 text-amber-800 border-amber-200 text-[10px]" data-testid={`admin-user-mcp-${u.email}`}>Pending first login</Badge>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-700">{fmtDate(u.last_login_at)}</td>
                      <td className="px-4 py-3 text-xs text-slate-700">{fmtDate(u.invite_sent_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={u.email === "demo@rciq.ai"}
                            onClick={() => setEditUser(u)}
                            data-testid={`admin-user-edit-${u.email}`}
                          >
                            <Pencil className="h-3 w-3 mr-1" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={u.email === "demo@rciq.ai"}
                            onClick={() => setConfirmState({
                              title: "Resend invite?", danger: false,
                              message: `Generate a fresh temp password for ${u.email}? Their current sessions will be ended.`,
                              confirmLabel: "Resend invite", run: () => resendInvite(u),
                            })}
                            data-testid={`admin-user-resend-${u.email}`}
                          >
                            <Mail className="h-3 w-3 mr-1" /> Resend
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={u.email === "demo@rciq.ai"}
                            onClick={() => setConfirmState({
                              title: "Reset password?", danger: false,
                              message: `Generate a new temp password for ${u.email}? Their current sessions will be ended.`,
                              confirmLabel: "Reset password", run: () => resetPw(u),
                            })}
                            data-testid={`admin-user-reset-${u.email}`}
                          >
                            <KeyRound className="h-3 w-3 mr-1" /> Reset
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" disabled={isSelf}
                            onClick={() => setConfirmState({
                              title: u.active ? "Deactivate user?" : "Reactivate user?", danger: u.active,
                              message: u.active
                                ? `Deactivate ${u.email}? They will be signed out immediately and unable to log back in.`
                                : `Reactivate ${u.email}? They will be able to sign in again.`,
                              confirmLabel: u.active ? "Deactivate" : "Reactivate",
                              run: () => toggleActive(u),
                            })}
                            data-testid={`admin-user-toggle-${u.email}`}
                          >
                            {u.active ? <><UserMinus className="h-3 w-3 mr-1" />Deactivate</> : <><UserPlus className="h-3 w-3 mr-1" />Reactivate</>}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50" disabled={isSelf || u.email === "demo@rciq.ai"}
                            onClick={() => setConfirmState({
                              title: "Delete user permanently?", danger: true,
                              message: `Permanently delete ${u.email} AND all their uploaded data? This cannot be undone.`,
                              confirmLabel: "Delete forever", run: () => deleteUser(u),
                            })}
                            data-testid={`admin-user-delete-${u.email}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        )}

        <p className="text-[11px] text-slate-400 mt-4">
          {users.length} {users.length === 1 ? "user" : "users"} · {orgs.length} {orgs.length === 1 ? "organization" : "organizations"} · Audit log captured for every change.
        </p>
      </div>

      <AddUserModal open={showAdd} onOpenChange={setShowAdd} onCreated={onCreated} onOrgCreated={onOrgCreated} organizations={orgs} />
      <EditUserModal open={Boolean(editUser)} onOpenChange={() => setEditUser(null)} user={editUser} organizations={orgs} onSaved={load} />
      <CredentialModal open={Boolean(credPayload)} onOpenChange={() => setCredPayload(null)} payload={credPayload} title={credTitle} />
      <ConfirmModal
        open={Boolean(confirmState)}
        onOpenChange={() => setConfirmState(null)}
        title={confirmState?.title}
        message={confirmState?.message}
        confirmLabel={confirmState?.confirmLabel}
        danger={confirmState?.danger}
        onConfirm={async () => { if (confirmState?.run) await confirmState.run(); }}
      />
    </AppShell>
  );
}
